"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Cpu, FileText, ShieldAlert, Wind, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const specCards = [{
  id: "bull-motion",
  code: "SPEC-01",
  title: "Bull & Motion System",
  description: "Check the published requirement for the exact model.",
  icon: Zap,
  tag: "Drive Assembly"
}, {
  id: "controller",
  code: "SPEC-02",
  title: "Controller",
  description: "Confirm how the supplied control system is powered and connected.",
  icon: Cpu,
  tag: "Operator Console"
}, {
  id: "inflatable-blower",
  code: "SPEC-03",
  title: "Inflatable Blower",
  description: "Check the requirements for the blower supplied or recommended for the inflatable.",
  icon: Wind,
  tag: "Air Support"
}];
export default function PowerRequirements() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="powerrequirementschecklist" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-12">
          {/* Header Zone */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="rounded-none border-border bg-muted/60 font-mono text-xs uppercase tracking-wider text-muted-foreground">
                ELECTRICAL ADVISORY // SEC-402
              </Badge>
              <span className="font-mono text-xs text-muted-foreground">
                PRE-INSTALLATION CHECKLIST
              </span>
            </div>

            <h2 className="font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Check Mechanical Bull Power Requirements Before Buying
            </h2>

            <div className="max-w-4xl space-y-3 pt-2 text-base text-muted-foreground sm:text-lg leading-relaxed">
              <p>
                Electrical requirements vary between mechanical bull systems. The
                bull, controller and inflatable blower may also have separate
                requirements, so buyers should check the documentation for the
                exact equipment.
              </p>
              <p>
                Before purchasing, confirm the required supply and make sure the
                intended location can support it. If electrical work is required,
                use an appropriately qualified professional and provide them
                with the actual equipment information rather than a generic
                specification from another machine.
              </p>
            </div>
          </div>

          {/* 3-Column Industrial Spec Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {specCards.map((card, i) => {
            const IconComponent = card.icon;
            return <motion.div key={card.id} data-index={i} variants={cardVariants} className="group relative flex flex-col justify-between border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary">
                  <div className="space-y-6">
                    {/* Datum Header Bar */}
                    <div className="flex items-center justify-between border-b border-border/70 pb-3">
                      <span className="font-mono text-xs tracking-widest text-muted-foreground">
                        {card.code}
                      </span>
                      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                        {card.tag}
                      </span>
                    </div>

                    {/* Icon & Title */}
                    <div className="space-y-3">
                      <div className="inline-flex size-10 items-center justify-center border border-border bg-muted/40 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <h3 className="font-serif text-xl font-bold uppercase tracking-normal sm:text-2xl">
                        {card.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  {/* Visual Datum Mark */}
                  <div className="mt-8 flex items-center justify-between border-t border-dashed border-border pt-3 font-mono text-[10px] text-muted-foreground">
                    <span>DOC VERIFY</span>
                    <span>REQUIRED //</span>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Full-width Industrial Advisory Notice Plate */}
          <motion.div variants={cardVariants} className="border-l-4 border-l-primary border-y border-r border-border bg-muted/30 p-6 sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="mt-0.5 shrink-0 text-primary">
                  <AlertTriangle className="size-6" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold uppercase tracking-wider text-primary">
                      CRITICAL NOTICE
                    </span>
                  </div>
                  <p className="font-serif text-lg font-bold uppercase tracking-tight text-foreground sm:text-xl">
                    Do not copy electrical requirements from another product simply because the machines look similar.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Equipment specifications differ by manufacturer, motor configuration, and controller build.
                  </p>
                </div>
              </div>

              {/* Action Link to Specifications / Contact */}
              <div className="flex shrink-0 flex-wrap items-center gap-3">
                <Button asChild variant="outline" className="rounded-none border-border font-mono text-xs uppercase">
                  <Link to="/SetupSafetyAndMaintenancePage">
                    <FileText className="mr-1.5 size-3.5" />
                    Setup Specs
                  </Link>
                </Button>
                <Button asChild variant="default" className="rounded-none font-mono text-xs uppercase">
                  <Link to="/ContactPage">
                    <ShieldAlert className="mr-1.5 size-3.5" />
                    Consult Team
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}